import clone from "clone";
import React from "react";
import { timeElapsedString } from '../../utils/date';
import { userUtils } from "../../utils/userUtils";
import { UserAvatar } from "../avatar/UserAvatar";
import { Button } from "../button/Button";
import { colors } from "../colors/color";
import { Divider } from "../Divider";
import { TextAreaField } from "../formField/textArea/TextAreaField";
import { Modal } from "../Modal";
import { Small, Strong } from "../typography/Body";
import { DeleteConfirmationButton } from "./DeleteConfirmationButton";
import { Flex } from "./FlexHelpers";
import { ScrollContainer } from "./ScrollContainer";
import { ChipPanel } from "../Chip";
import { InfoChip, ToggleChip } from "../Chip";

const defaultCommentTexts = 
{
    deleteModalHeading: "Slet kommentaren",
    deleteModalConfirmation: "Er du sikker på, at du vil slette kommentaren?",
    addButton: "Tilføj kommentar",
    noComments: "Ingen kommentarer oprettet",
    deletedConfirmation: "Kommentaren er slettet",
    editModalHeader: "Rediger kommentar",
    editModalLabel: "Kommentar"
};
type Message = {
    id: string,
    content: string,
    tags?:string[]
    createdBy: { userId: string, displayName: string; },
    created: Date,
    updated: Date | null,
    deleted: boolean;
    onEdit?: (id:string, newContent: string, tags?: string[]) => Promise<{ errorMessage?: string; }>;
    onDelete?: (id:string) => Promise<{ errorMessage?: string; }>;
}

export type BaseMessagePanelProps = {
    messages: Message[],
    availableTags?: string[],
    texts?:{ 
        deleteModalHeading: string,
        deleteModalConfirmation: string,
        addButton:string,
        noComments: string,
        deletedConfirmation: string,
        editModalHeader: string,
        editModalLabel: string
    },
    onCreate?: (content: string, tags?: string[]) => Promise<{ errorMessage?: string; }>;
    inlineCreate?: boolean,
    createDisabled?: boolean,
    createDisabledTooltip?: string,
    messageValidator?: (content: string) => { errorMessage?: string; },
    maxHeightPx?: number,
};

export function BaseMessagePanel (incomingProps: BaseMessagePanelProps): JSX.Element 
{
    // Apply default texts for any missing text props
    const props = { ...incomingProps, texts: { ...defaultCommentTexts, ...incomingProps.texts } };

    const [newMessage, setNewMessage] = React.useState<boolean>(false);
    const [editMessage, setEditMessage] = React.useState<Message | null>(null);

    async function handleClickAddMessage (content:string, tags?: string[]): Promise<{ errorMessage?: string; }>
    {
        if(props.onCreate)
        {
            const result = await props.onCreate(content, tags);
            return result;
        }
        else            
            throw new Error('Create function not provided');
    }

    async function handleClickEditMessage (updatedMessage:Message, tags?:string[]): Promise<{ errorMessage?: string; }>
    {
        if(updatedMessage.onEdit)
        {
            const result = await updatedMessage.onEdit(updatedMessage.id, updatedMessage.content, tags);
            return result;
        }
        else
            throw new Error('Edit function not provided');
    }

    const sortedMessages = React.useMemo(() =>
    {
        const messagesCopy = clone(props.messages);
        messagesCopy.sort((a, b) =>
        {
            return b.created.getTime() - a.created.getTime();
        });
        return messagesCopy;
    }, [props.messages]);

    return (
        <Flex flexDirection="column" justifyContent='stretch' grow='1' gap={8}>

            {props.onCreate && props.inlineCreate &&
                <Flex flexDirection="column" gap={8}>
                    <InlineCreateMessage
                        texts={props.texts}
                        availableTags={props.availableTags}
                        disabled={props.createDisabled}
                        disabledTooltip={props.createDisabledTooltip}
                        onSave={(content, tags) => handleClickAddMessage(content, tags)}
                    />
                    {props.messages.length > 0 &&
                        <Divider variant="light" />
                    }
                </Flex>
            }

            <Flex flexDirection="column" justifyContent='stretch' grow='1'>


                <ScrollContainer maxHeightPx={props.maxHeightPx}>
                    <Flex flexDirection="column" gap={8}>
                    {sortedMessages.map(c =>
                    {
                        let htmlContent = c.content.replace(/\r\n|\r|\n/g, "<br />");
                        if (c.updated && !c.deleted)
                            htmlContent += ' <br /><Small>(redigeret)</Small>';

                        const timeStamp = timeElapsedString(c.created);
                        return (

                            <Flex key={c.id} flexDirection="column" gap={2} style={{ padding: '5px', marginBottom: '5px' }}>
                                
                                

                                <Flex alignItems="center" gap={8}>

                                    <div style={{ minWidth: '32px' }}>
                                        <UserAvatar size="24px"
                                            user={{ ...c.createdBy, initials: userUtils.getInitials(c.createdBy.displayName) }}
                                        />
                                    </div>

                                    <Flex alignItems="center" gap={8} style={{ minHeight: '32px' }}>
                                        <Strong style={{ fontSize: 14 }}>{c.createdBy.displayName}</Strong>
                                        <span style={{ fontSize: 14, color: colors.text.gray.normal }}>{timeStamp}</span>
                                    </Flex>

                                    {props.availableTags && c.tags &&
                                        <ChipPanel style={{ marginLeft: 32 }}>
                                            {c.tags.map(tag => (
                                                <InfoChip 
                                                    key={tag}
                                                    colorHash={tag}
                                                >
                                                    {tag}
                                                </InfoChip>
                                            ))}
                                        </ChipPanel>
                                    }

                                    {(c.onEdit || c.onDelete) && !c.deleted &&
                                        <Flex justifyContent="flex-end" gap={4} style={{ flexGrow: 1 }}>
                                            {c.onEdit &&
                                                <Button variant="text" text="Rediger" size="22px"
                                                    onClick={() => { setEditMessage(c); }} />
                                            }

                                            {c.onDelete &&
                                                <DeleteConfirmationButton
                                                    confirmationHeading={props.texts.deleteModalHeading}
                                                    requireKey={false}
                                                    onConfirmDelete={async () => await c.onDelete?.(c.id)}
                                                    buttonText="Slet"
                                                    buttonSize="22px"
                                                    variant="text"
                                                    hideIcon
                                                    confirmationText={props.texts.deleteModalConfirmation}
                                                />
                                            }
                                        </Flex>
                                    }
                                </Flex>

                                <Flex marginLeft={32 + 8}>
                                    {c.deleted &&
                                        <div style={{ fontSize: 14, fontStyle: 'italic', color: colors.text.gray.normal }}>
                                            {props.texts.deletedConfirmation}
                                        </div>
                                    }

                                    {!c.deleted && c.updated &&
                                        <div style={{ fontSize: 14 }} dangerouslySetInnerHTML={{ __html: htmlContent }} />
                                    }

                                    {!c.deleted && !c.updated &&
                                        <div style={{ fontSize: 14 }} dangerouslySetInnerHTML={{ __html: htmlContent }} />
                                    }
                                </Flex>

                            </Flex>

                        );
                    })}
                    
                    {props.messages.length === 0 && (
                        <Small>{props.texts.noComments}</Small>
                    )}

                </Flex>
                </ScrollContainer>

            </Flex>

            {props.onCreate &&

                <Flex flexDirection="column" gap={8}>

                    {!props.inlineCreate &&
                        <>
                            {props.messages.length > 0 &&
                                <Divider variant="light" />
                            }

                            <Flex justifyContent="flex-end">
                                <Button variant="outlined" onClick={() => setNewMessage(true)} text={props.texts.addButton} disabled={props.createDisabled} toolTipText={props.createDisabledTooltip} />
                            </Flex>

                            {/* For creating new messages */}
                            <EditModal
                                isNew={true}
                                show={newMessage}
                                texts={props.texts}
                                message={''}
                                initialTags={[]}
                                availableTags={props.availableTags}
                                onSave={(newMessage, tags) => handleClickAddMessage(newMessage, tags)}
                                onClose={() => { setNewMessage(false); }}
                            />
                        </>
                    }

                    {/* For editing existing messages*/}
                    <EditModal
                        isNew={false}
                        show={editMessage !== null}
                        texts={props.texts}
                        message={editMessage?.content || ''}
                        initialTags={editMessage?.tags}
                        availableTags={props.availableTags}
                        onSave={(updatedContent, tags) => 
                        {
                            if(editMessage)
                            {
                                return handleClickEditMessage(
                                { 
                                    ...editMessage,
                                    tags: tags || [],
                                    content: updatedContent 
                                }, tags || []);
                            }
                            else
                                throw new Error("No message to edit");
                        }}
                        onClose={() => { setEditMessage(null); }}
                    />
                </Flex>
            }
        </Flex>
    );
}

type InlineCreateMessageProps = {
    availableTags?: string[],
    disabled?: boolean,
    disabledTooltip?: string,
    texts: {
        addButton: string,
        editModalLabel: string,
    },
    onSave: (content: string, tags?: string[]) => Promise<{ errorMessage?: string; }>;
};

function InlineCreateMessage (props: InlineCreateMessageProps): JSX.Element
{
    const [content, setContent] = React.useState<string>('');
    const [selectedTags, setSelectedTags] = React.useState<string[]>([]);
    const [errorMessage, setErrorMessage] = React.useState<string>('');
    const [saving, setSaving] = React.useState<boolean>(false);

    async function handleClickSave ()
    {
        setSaving(true);
        const result = await props.onSave(content, props.availableTags === undefined ? undefined : selectedTags);
        setSaving(false);
        if (result.errorMessage)
            setErrorMessage(result.errorMessage);
        else
        {
            setContent('');
            setSelectedTags([]);
            setErrorMessage('');
        }
    }

    function handleTagClick (tag: string)
    {
        if(selectedTags.includes(tag))
            setSelectedTags(selectedTags.filter(t => t !== tag));
        else
            setSelectedTags([...selectedTags, tag]);
    }

    return (
        <Flex flexDirection="column" gap={8}>
            {props.availableTags &&
                <ChipPanel>
                    {props.availableTags.map(tag =>
                        <ToggleChip key={tag}
                            selected={selectedTags.includes(tag)}
                            selectedStyle={{ colorHash: tag }}
                            onClick={() => handleTagClick(tag)}>{tag}</ToggleChip>
                    )}
                </ChipPanel>
            }
            <TextAreaField
                label={props.texts.editModalLabel}
                errorMessage={errorMessage}
                value={content}
                onChange={setContent}
            />
            <Flex justifyContent="flex-end">
                <Button variant="outlined"
                    size="32px"
                    onClick={handleClickSave}
                    text={props.texts.addButton}
                    disabled={props.disabled || saving || content.trim().length === 0}
                    toolTipText={props.disabledTooltip} />
            </Flex>
        </Flex>
    );
}

type EditModalProps = {
    show: boolean,
    isNew:boolean,
    message: string,
    availableTags?: string[],
    initialTags?: string[],
    texts:{
        editModalHeader: string,
        editModalLabel: string,
    }
    onSave: (content:string, tags?:string[]) => Promise<{ errorMessage?: string; }>;
    onClose: () => void;
};

function EditModal (props: EditModalProps): JSX.Element 
{
    const [editedMessage, setEditedMessage] = React.useState<string>(props.message);
    const [selectedTags, setSelectedTags] = React.useState<string[]>(props.initialTags || []);
    const [errorMessage, setErrorMessage] = React.useState<string>('');

    React.useEffect(() =>
    {
        setEditedMessage(props.message);
        setSelectedTags(props.initialTags || []);
        setErrorMessage('');
    }, [props.message, props.show]);

    async function handleClickSave ()
    {
        const result = await props.onSave(editedMessage, props.availableTags === undefined ? undefined : selectedTags);
        if (result.errorMessage)
            setErrorMessage(result.errorMessage);
        else
            props.onClose();
    }

    function handleTagClick (tag: string)
    {
        if(selectedTags.includes(tag))
            setSelectedTags(selectedTags.filter(t => t !== tag));
        else
            setSelectedTags([...selectedTags, tag]);
    }

    return (
        <Modal
            onCancel={props.onClose}
            title={props.texts.editModalHeader}
            showModal={props.show}
            content={
                <Flex flexDirection="column" gap={16}>
                    <ChipPanel> 
                    {props.availableTags && props.availableTags.map(tag => 
                        <ToggleChip key={tag} 
                            selected={selectedTags.includes(tag)} 
                            selectedStyle={{ colorHash: tag }}
                            onClick={() => handleTagClick(tag)}>{tag}</ToggleChip>
                    )}
                    </ChipPanel>
                    <TextAreaField
                        label={props.texts.editModalLabel}
                        errorMessage={errorMessage}
                        value={editedMessage}
                        onChange={setEditedMessage}
                    />
                    <Flex justifyContent="flex-end" gap={8}>
                        <Button variant="outlined" onClick={handleClickSave} text={props.isNew ? "Opret" : "Gem"} />
                        <Button variant="outlined" onClick={props.onClose} text="Annuller" />
                    </Flex>
                </Flex>
            } />
    );
}